import * as Tone from 'tone';

/**
 * ChannelStrip.js
 * Represents a standard mixer channel strip.
 * Chain: Source -> EQ3 -> Panner -> Volume -> Output.
 * Also handles Post-Fader Sends.
 */
export class ChannelStrip {
    /**
     * @param {Object} source - The source synth or signal (must have .connect()).
     * @param {Object} mixTraits - Configuration { eq, pan, sends, compression }.
     * @param {Object} globalSends - Dictionary of global send destinations { reverb: node, delay: node }.
     */
    constructor(source, mixTraits = {}, globalSends = {}) {
        this.source = source;
        this.sends = [];
        this.disposed = false;

        // 1. Create Nodes
        this.input = new Tone.Gain(1); // Input Stage

        // Sidechain Gain (For auto-ducking "Clarity Protocol")
        this.sidechainGain = new Tone.Gain(1);

        // Task 1: Signal Hygiene (HighPass Filter)
        // Default to 0Hz (open). If instrument is not Anchor, we set to 120Hz in applyTraits.
        this.hpf = new Tone.Filter(0, "highpass");

        this.eq = new Tone.EQ3({
            low: 0,
            mid: 0,
            high: 0,
            lowFrequency: 400,
            highFrequency: 2500
        });

        // Panner (Tone.Panner is simple stereo panning)
        this.panner = new Tone.Panner(0);

        // Fader
        this._fader = new Tone.Volume(0);

        // Output
        this.output = new Tone.Gain(1);

        // 2. Build Chain: Input -> Sidechain -> HPF -> EQ -> Panner -> Fader -> Output
        this.input.chain(this.sidechainGain, this.hpf, this.eq, this.panner, this._fader, this.output);

        // 3. Connect Source
        // If source is a SynthWrapper or Tone Synth, it has .connect()
        if (source && source.connect) {
            source.connect(this.input);
        } else {
            console.warn('[ChannelStrip] Invalid source passed', source);
        }

        // 4. Apply Traits
        this.applyTraits(mixTraits, globalSends);
    }

    applyTraits(traits, globalSends) {
        if (!traits) return;

        // Task 1: Signal Hygiene & Warmth Restoration (Smart Filtering)
        // Role-Based Filtering to prevent "Thinness"
        // Soprano/Alto (High/Mid) -> 120Hz
        // Tenor/Baritone (Mid-Low) -> 80Hz
        // Bass/Kick (Low) -> 20Hz (Rumble cut only)

        const spectralRole = traits.spectral_role || 'mid'; // Default to mid if unknown
        const section = traits.section || 'mid';

        // Determine Frequency Cutoff
        let cutoff = 120; // Default High/Mid

        if (traits.spectral_floor !== undefined) {
            // Task 2: Exact Physics (Explicit Floor)
            cutoff = traits.spectral_floor;
        } else if (traits.role === 'bass' || traits.role === 'anchor' || spectralRole === 'low' || section === 'low') {
            // Bass/Kick
            cutoff = 20;
        } else if (spectralRole === 'mid' || section === 'mid') {
            // Tenor/Baritone (Warmth Zone)
            // If it's explicitly 'mid', allow more body
            cutoff = 80;
        } else {
            // High/Full (Soprano/Alto)
            cutoff = 120;
        }

        this.hpf.frequency.value = cutoff;

        // EQ
        if (traits.eq) {
            // Mapping boost/cuts to 3-band is approximate logic or specific
            // Since we use EQ3 (Low/Mid/High), we can't map specific parametric cuts exactly without a Multiband or ParametricEQ.
            // For now, we simulate broad strokes.
            // Future improvement: Use Tone.ParametricEQ for specific cuts.

            // If the trait specifies generic low/mid/high gains directly (implied in GenreLibrary updates earlier)
            // But InstrumentLibrary uses { lowCut, boost: [{freq, gain}] } style.
            // We'll interpret standard boosts for EQ3.

            // Default flat
            this.eq.low.value = 0;
            this.eq.mid.value = 0;
            this.eq.high.value = 0;

            // Simple Logic: Check for keywords in traits or boost arrays
            if (traits.eq.lowCut > 100) this.eq.low.value -= 2; // Reduce low
            if (traits.eq.highCut < 5000) this.eq.high.value -= 2; // Reduce high

            if (traits.eq.boost) {
                traits.eq.boost.forEach(b => {
                    if (b.freq < 400) this.eq.low.value += b.gain;
                    else if (b.freq < 2500) this.eq.mid.value += b.gain;
                    else this.eq.high.value += b.gain;
                });
            }
            if (traits.eq.cuts) {
                 traits.eq.cuts.forEach(c => {
                    if (c.freq < 400) this.eq.low.value += c.gain;
                    else if (c.freq < 2500) this.eq.mid.value += c.gain;
                    else this.eq.high.value += c.gain;
                });
            }
        }

        // Pan
        if (traits.pan) {
            if (traits.pan.fixed !== undefined) {
                this.panner.pan.value = traits.pan.fixed;
            }
            // 'width' strategy usually handled by global placement logic or ignored for mono strip
        }

        // Sends
        if (traits.sends && globalSends) {
            Object.entries(traits.sends).forEach(([key, level]) => {
                const dest = globalSends[key];
                if (dest) {
                    // Create Send Gain (Post-Fader: Connect from this._fader)
                    // Note: Tone.Volume connects to Output.
                    // We branch off Volume -> SendGain -> Dest.
                    const sendGain = new Tone.Gain(level);
                    this._fader.connect(sendGain);
                    sendGain.connect(dest);
                    this.sends.push(sendGain);
                }
            });
        }

        // Compression (Simple placeholder if we wanted channel comp)
        // Ignoring for now as per plan (MasteringChain handles the big dynamics, or internal synth chain)
    }

    // --- Connectivity ---

    connect(destination) {
        this.output.connect(destination);
        return this;
    }

    disconnect(destination) {
        if (destination) this.output.disconnect(destination);
        else this.output.disconnect();
        return this;
    }

    dispose() {
        if (this.disposed) return;
        this.disposed = true;

        // Disconnect source
        // We do NOT dispose the source here if it's managed externally,
        // BUT current architecture implies 1:1 ownership.
        // SynthFactory wrappers usually handle their own dispose.
        if (this.source && typeof this.source.dispose === 'function') {
            this.source.dispose();
        }

        // Clean up nodes
        this.sends.forEach(s => {
            s.disconnect();
            s.dispose();
        });

        this.input.dispose();
        this.sidechainGain.dispose();
        this.hpf.dispose();
        this.eq.dispose();
        this.panner.dispose();
        this._fader.dispose();
        this.output.dispose();
    }

    // --- Proxy Methods to Source (Mimicking Synth) ---

    triggerAttackRelease(...args) {
        if (this.source && this.source.triggerAttackRelease) {
            this.source.triggerAttackRelease(...args);
        }
    }

    triggerAttack(...args) {
         if (this.source && this.source.triggerAttack) {
            this.source.triggerAttack(...args);
        }
    }

    triggerRelease(...args) {
         if (this.source && this.source.triggerRelease) {
            this.source.triggerRelease(...args);
        }
    }

    releaseAll(...args) {
         if (this.source && this.source.releaseAll) {
            this.source.releaseAll(...args);
        }
    }

    setArticulation(articulation) {
        if (this.source && this.source.setArticulation) {
            this.source.setArticulation(articulation);
        }
    }

    // --- Automation Properties ---

    /**
     * Expose volume param for ramping.
     * We map this to the Channel Strip Fader, NOT the inner synth.
     */
    get volume() {
        return this._fader.volume;
    }

    get pan() {
        return this.panner.pan;
    }
}
